<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';

import { buildRuleStatusOptions } from './utils/automationOperationsConstants';

defineProps({
  totalCount: { type: Number, default: 0 },
  isLoading: { type: Boolean, default: false },
});

defineEmits(['apply']);

const filters = defineModel('filters', {
  type: Object,
  required: true,
});

const { t } = useI18n();

const ruleStatusOptions = computed(() => buildRuleStatusOptions(t));
</script>

<template>
  <div
    class="mb-3 flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between"
  >
    <div class="flex min-w-0 flex-col gap-1">
      <span class="text-heading-3 text-n-slate-12">
        {{ $t('AUTOMATION.OPERATIONS.RULES.TITLE') }}
      </span>
      <span class="text-body-main text-n-slate-11">
        {{
          $t('AUTOMATION.OPERATIONS.RULES.TOTAL', {
            count: totalCount || 0,
          })
        }}
      </span>
    </div>
    <form
      class="grid gap-3 md:grid-cols-[minmax(12rem,1fr)_minmax(10rem,0.8fr)_9rem_7rem_auto] md:items-end"
      data-test="automation-rule-filter-form"
      @submit.prevent="$emit('apply')"
    >
      <label class="flex min-w-0 flex-col gap-1">
        <span class="text-label-small text-n-slate-11">
          {{ $t('AUTOMATION.OPERATIONS.RULES.SEARCH') }}
        </span>
        <input
          v-model="filters.q"
          type="text"
          data-test="automation-rule-filter-q"
          class="h-8 rounded-md border border-n-weak bg-n-surface-1 px-2 text-body-main text-n-slate-12"
        />
      </label>
      <label class="flex min-w-0 flex-col gap-1">
        <span class="text-label-small text-n-slate-11">
          {{ $t('AUTOMATION.OPERATIONS.RULES.EVENT') }}
        </span>
        <input
          v-model="filters.eventName"
          type="text"
          data-test="automation-rule-filter-event"
          class="h-8 rounded-md border border-n-weak bg-n-surface-1 px-2 text-body-main text-n-slate-12"
        />
      </label>
      <label class="flex min-w-0 flex-col gap-1">
        <span class="text-label-small text-n-slate-11">
          {{ $t('AUTOMATION.OPERATIONS.RULES.STATUS') }}
        </span>
        <select
          v-model="filters.status"
          data-test="automation-rule-filter-status"
          class="h-8 rounded-md border border-n-weak bg-n-surface-1 px-2 text-body-main text-n-slate-12"
        >
          <option
            v-for="option in ruleStatusOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </label>
      <label class="flex min-w-0 flex-col gap-1">
        <span class="text-label-small text-n-slate-11">
          {{ $t('AUTOMATION.OPERATIONS.RULES.WINDOW') }}
        </span>
        <input
          v-model.number="filters.windowHours"
          type="number"
          min="1"
          max="720"
          data-test="automation-rule-filter-window"
          class="h-8 rounded-md border border-n-weak bg-n-surface-1 px-2 text-body-main text-n-slate-12"
        />
      </label>
      <Button
        :label="$t('AUTOMATION.OPERATIONS.RULES.APPLY')"
        icon="i-lucide-filter"
        color="slate"
        variant="outline"
        size="sm"
        :is-loading="isLoading"
        type="submit"
      />
    </form>
  </div>
</template>
